import { useState } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { Editor } from '@/components/Editor';
import { StageEditor, Stage, Challenge, Reward } from '@/components/Adventures/StageEditor';

/**
 * Item recipe option.
 */
interface ItemRecipeOption {
    id: number;
    name: string;
    rarity: string;
    learnable_by: 'no' | 'blueprint' | 'building';
}

/**
 * Tag option.
 */
interface TagOption {
    id: number;
    name: string;
}

/**
 * Rarity option.
 */
interface RarityOption {
    value: string;
    label: string;
}

/**
 * Reward type option.
 */
interface RewardTypeOption {
    value: string;
    label: string;
}

/**
 * Enclave option for requirements.
 */
interface EnclaveOption {
    id: number;
    name: string;
    owner_name: string | null;
}

/**
 * Requirement data for the form.
 */
interface Requirement {
    type: 'item' | 'reputation' | 'reputation_max';
    item_id: number | null;
    enclave_id: number | null;
    minimum_reputation: number;
    maximum_reputation: number;
}

/**
 * Reputation levels for requirements.
 * Value is the minimum reputation needed to be at that level.
 */
const REPUTATION_LEVELS = [
    { value: -3000, label: 'Hated' },
    { value: -2000, label: 'Hostile' },
    { value: -1000, label: 'Unfriendly' },
    { value: 0, label: 'Neutral' },
    { value: 1, label: 'Friendly' },
    { value: 1001, label: 'Honored' },
    { value: 3001, label: 'Revered' },
    { value: 6001, label: 'Exalted' },
];

/**
 * Predefined material types for rewards.
 */
const MATERIAL_TYPES = [
    { value: 'tea', label: '🍵 Tea' },
    { value: 'wood', label: '🪵 Wood' },
    { value: 'mystical', label: '✨ Mystical' },
    { value: 'medicinal', label: '💊 Medicinal' },
    { value: 'edible', label: '🍎 Edible' },
];

/**
 * Props for the Create page.
 */
interface Props {
    stats: string[];
    skills: string[];
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
    rewardTypes: RewardTypeOption[];
    rarities: RarityOption[];
    itemRecipes: ItemRecipeOption[];
    allTags: TagOption[];
    enclaves: EnclaveOption[];
}

/**
 * GM Adventures Create page - form to create a new adventure.
 */
export default function Create({ stats, skills, statLabels, skillLabels, rewardTypes, rarities, itemRecipes, allTags, enclaves }: Props) {
    // Basic adventure fields
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [dialogueStart, setDialogueStart] = useState('');
    const [dialogueSuccess, setDialogueSuccess] = useState('');
    const [dialogueFailure, setDialogueFailure] = useState('');
    const [isActive, setIsActive] = useState(true);

    // Challenges, rewards, and requirements
    const [challenges, setChallenges] = useState<Challenge[]>([]);
    const [rewards, setRewards] = useState<Reward[]>([]);
    const [requirements, setRequirements] = useState<Requirement[]>([]);

    // Form state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Multi-stage state
    const [isMultiStage, setIsMultiStage] = useState(false);
    const [stages, setStages] = useState<Stage[]>([]);

    // Combine stats and skills for challenge selection
    const allStatTypes = [
        ...stats.map(s => ({ value: s, label: statLabels[s] || s, type: 'stat' })),
        ...skills.map(s => ({ value: s, label: skillLabels[s] || s, type: 'skill' })),
    ];

    /**
     * Add a new challenge.
     */
    const addChallenge = () => {
        setChallenges([...challenges, { stat_type: stats[0] || '', difficulty: 10 }]);
    };

    /**
     * Remove a challenge.
     */
    const removeChallenge = (index: number) => {
        setChallenges(challenges.filter((_, i) => i !== index));
    };

    /**
     * Update a challenge.
     */
    const updateChallenge = (index: number, field: keyof Challenge, value: string | number) => {
        const updated = [...challenges];
        updated[index] = { ...updated[index], [field]: value };
        setChallenges(updated);
    };

    /**
     * Add a new reward.
     */
    const addReward = () => {
        setRewards([...rewards, {
            reward_type: 'item',
            item_recipe_id: null,
            item_rarity: null,
            material_type: null,
            enclave_id: null,
            reputation_min: null,
            reputation_max: null,
            quantity_min: 1,
            quantity_max: 1,
            chance_percent: 100,
            tags: [],
        }]);
    };

    /**
     * Remove a reward.
     */
    const removeReward = (index: number) => {
        setRewards(rewards.filter((_, i) => i !== index));
    };

    /**
     * Update a reward.
     */
    const updateReward = (index: number, field: keyof Reward, value: unknown) => {
        const updated = [...rewards];
        updated[index] = { ...updated[index], [field]: value };
        // Auto-set max to min when min changes (for quantity and reputation)
        if (field === 'quantity_min' && typeof value === 'number') {
            updated[index].quantity_max = value;
        }
        if (field === 'reputation_min' && typeof value === 'number') {
            updated[index].reputation_max = value;
        }
        setRewards(updated);
    };

    /**
     * Add a new requirement.
     */
    const addRequirement = () => {
        setRequirements([...requirements, {
            type: 'item',
            item_id: null,
            enclave_id: null,
            minimum_reputation: 0,
            maximum_reputation: 6001,
        }]);
    };

    /**
     * Remove a requirement.
     */
    const removeRequirement = (index: number) => {
        setRequirements(requirements.filter((_, i) => i !== index));
    };

    /**
     * Update a requirement.
     */
    const updateRequirement = (index: number, field: keyof Requirement, value: unknown) => {
        const updated = [...requirements];
        updated[index] = { ...updated[index], [field]: value };
        setRequirements(updated);
    };

    /**
     * Change requirement type and reset related fields.
     */
    const changeRequirementType = (index: number, type: 'item' | 'reputation' | 'reputation_max') => {
        const updated = [...requirements];
        updated[index] = {
            type,
            item_id: null,
            enclave_id: null,
            minimum_reputation: 0,
            maximum_reputation: 6001,
        };
        setRequirements(updated);
    };

    // Stage handlers
    const addStage = () => {
        const newStageNumber = stages.length + 1;
        setStages([...stages, {
            stage_number: newStageNumber,
            dialogue_pre: '',
            dialogue_success: '',
            dialogue_failure: '',
            challenges: [],
            rewards: [],
        }]);
    };

    const removeStage = (index: number) => {
        const newStages = stages.filter((_, i) => i !== index);
        // Renumber remaining stages
        newStages.forEach((stage, i) => {
            stage.stage_number = i + 1;
        });
        setStages(newStages);
    };

    const updateStage = (index: number, field: keyof Stage, value: unknown) => {
        const updated = [...stages];
        updated[index] = { ...updated[index], [field]: value };
        setStages(updated);
    };

    const moveStageUp = (index: number) => {
        if (index === 0) return;
        const newStages = [...stages];
        [newStages[index - 1], newStages[index]] = [newStages[index], newStages[index - 1]];
        // Renumber stages
        newStages.forEach((stage, i) => {
            stage.stage_number = i + 1;
        });
        setStages(newStages);
    };

    const moveStageDown = (index: number) => {
        if (index === stages.length - 1) return;
        const newStages = [...stages];
        [newStages[index], newStages[index + 1]] = [newStages[index + 1], newStages[index]];
        // Renumber stages
        newStages.forEach((stage, i) => {
            stage.stage_number = i + 1;
        });
        setStages(newStages);
    };

    /**
     * Handle form submission.
     */
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        // Transform rewards to ensure reward_type is a string value
        const transformReward = (reward: Reward) => ({
            ...reward,
            reward_type: typeof reward.reward_type === 'object' && reward.reward_type !== null
                ? (reward.reward_type as { value: string }).value
                : reward.reward_type,
        });

        const transformedRewards = rewards.map(transformReward);

        // Build the submission data
        const data: Record<string, unknown> = {
            name,
            description: description || null,
            dialogue_start: dialogueStart || null,
            dialogue_success: dialogueSuccess || null,
            dialogue_failure: dialogueFailure || null,
            is_active: isActive,
            is_multi_stage: isMultiStage,
            requirements: requirements.length > 0 ? requirements : null,
        };

        if (isMultiStage) {
            // Multi-stage: include stages with their challenges and rewards
            data.stages = stages.map(stage => ({
                stage_number: stage.stage_number,
                dialogue_pre: stage.dialogue_pre || null,
                dialogue_success: stage.dialogue_success || null,
                dialogue_failure: stage.dialogue_failure || null,
                challenges: stage.challenges,
                rewards: stage.rewards.map(transformReward),
            }));
        } else {
            // Single-stage: include challenges and rewards directly
            data.challenges = challenges;
            data.rewards = transformedRewards;
        }

        router.post('/gm/adventures', data, {
            onError: (errs) => {
                setErrors(errs);
                setIsSubmitting(false);
            },
            onSuccess: () => setIsSubmitting(false),
        });
    };

    return (
        <GmLayout title="Create Adventure" activePath="/gm/adventures">
            <div className="max-w-4xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">Create Adventure</h1>
                    <Link href="/gm/adventures" className="text-gray-400 hover:text-white transition-colors">
                        ← Back to Adventures
                    </Link>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Info Section */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-white mb-4">Basic Information</h2>

                        {/* Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Name <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                placeholder="Dragon's Lair"
                                required
                            />
                            {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name}</p>}
                        </div>

                        {/* Description - Markdown Editor */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                            <div data-color-mode="dark">
                                <Editor
                                    value={description}
                                    onChange={(value) => setDescription(value)}
                                    placeholder="A dangerous adventure into the dragon's lair..."
                                    height={150}
                                />
                            </div>
                        </div>

                        {/* Active Status */}
                        <div className="flex items-center">
                            <label className="flex items-center gap-2 text-sm text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                    className="rounded bg-slate-700 border-slate-600 text-purple-600"
                                />
                                Active (available to players)
                            </label>
                        </div>

                        {/* Multi-Stage Toggle */}
                        <div className="pt-4 border-t border-slate-700">
                            <label className="flex items-center gap-2 text-sm text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={isMultiStage}
                                    onChange={(e) => setIsMultiStage(e.target.checked)}
                                    className="rounded bg-slate-700 border-slate-600 text-purple-600"
                                />
                                Multi-Stage Adventure
                            </label>
                            <p className="text-xs text-gray-500 mt-1 ml-6">
                                Multi-stage adventures have multiple phases with their own challenges, rewards, and dialogue.
                                Characters take damage based on stage outcomes.
                            </p>
                        </div>
                    </div>

                    {/* Single-stage: Dialogue, Challenges, and Rewards - only when NOT multi-stage */}
                    {!isMultiStage && (
                    <>
                    {/* Dialogue Section - Markdown Editors */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-white mb-4">Dialogue</h2>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Start Dialogue</label>
                            <div data-color-mode="dark">
                                <Editor
                                    value={dialogueStart}
                                    onChange={(value) => setDialogueStart(value)}
                                    placeholder="You approach the dragon's lair..."
                                    height={120}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-green-400 mb-1">Success Dialogue</label>
                            <div data-color-mode="dark">
                                <Editor
                                    value={dialogueSuccess}
                                    onChange={(value) => setDialogueSuccess(value)}
                                    placeholder="You defeated the dragon!"
                                    height={120}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-red-400 mb-1">Failure Dialogue</label>
                            <div data-color-mode="dark">
                                <Editor
                                    value={dialogueFailure}
                                    onChange={(value) => setDialogueFailure(value)}
                                    placeholder="The dragon was too powerful..."
                                    height={120}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Challenges Section */}
                    <ChallengesSection
                        challenges={challenges}
                        allStatTypes={allStatTypes}
                        onAdd={addChallenge}
                        onRemove={removeChallenge}
                        onUpdate={updateChallenge}
                        errors={errors}
                    />

                    {/* Rewards Section */}
                    <RewardsSection
                        rewards={rewards}
                        rewardTypes={rewardTypes}
                        rarities={rarities}
                        itemRecipes={itemRecipes}
                        allTags={allTags}
                        enclaves={enclaves}
                        onAdd={addReward}
                        onRemove={removeReward}
                        onUpdate={updateReward}
                        errors={errors}
                    />
                    </>
                    )}

                    {/* Multi-stage: Stages Section - only when multi-stage */}
                    {isMultiStage && (
                    <>
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-white">Stages</h2>
                            <button
                                type="button"
                                onClick={addStage}
                                className="px-3 py-1 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                            >
                                + Add Stage
                            </button>
                        </div>

                        {/* Stage info box */}
                        <div className="mb-4 p-4 bg-slate-700/50 border border-slate-600 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-purple-400">ℹ</span>
                                <h3 className="text-sm font-medium text-gray-300">About Multi-Stage Adventures</h3>
                            </div>
                            <p className="text-xs text-gray-400">
                                Each stage has its own challenges, rewards, and dialogue. Stages are completed in order,
                                with 10 minutes per stage. Characters take damage based on outcomes: Success (10% HP),
                                Partial (10-50% HP), Failure (100% HP). Party members take 5% HP per stage.
                                Between stages, characters can heal using their healing skill and consumable items.
                            </p>
                        </div>

                        {stages.length === 0 ? (
                            <p className="text-gray-400 text-sm">No stages added. Add at least one stage.</p>
                        ) : (
                            <div className="space-y-4">
                                {stages.map((stage, index) => (
                                    <StageEditor
                                        key={index}
                                        stage={stage}
                                        stageIndex={index}
                                        totalStages={stages.length}
                                        allStatTypes={allStatTypes}
                                        rewardTypes={rewardTypes}
                                        rarities={rarities}
                                        itemRecipes={itemRecipes}
                                        allTags={allTags}
                                        enclaves={enclaves}
                                        onUpdate={updateStage}
                                        onRemove={removeStage}
                                        onMoveUp={moveStageUp}
                                        onMoveDown={moveStageDown}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                    </>
                    )}

                    {/* Requirements Section */}
                    <RequirementsSection
                        requirements={requirements}
                        itemRecipes={itemRecipes}
                        enclaves={enclaves}
                        onAdd={addRequirement}
                        onRemove={removeRequirement}
                        onUpdate={updateRequirement}
                        onTypeChange={changeRequirementType}
                        errors={errors}
                    />

                    {/* Submit Buttons */}
                    <div className="flex gap-4">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors disabled:opacity-50"
                        >
                            {isSubmitting ? 'Creating...' : 'Create Adventure'}
                        </button>
                        <Link
                            href="/gm/adventures"
                            className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        >
                            Cancel
                        </Link>
                    </div>
                </form>
            </div>
        </GmLayout>
    );
}


/**
 * Challenges section component.
 */
interface ChallengesSectionProps {
    challenges: Challenge[];
    allStatTypes: { value: string; label: string; type: string }[];
    onAdd: () => void;
    onRemove: (index: number) => void;
    onUpdate: (index: number, field: keyof Challenge, value: string | number) => void;
    errors: Record<string, string>;
}

function ChallengesSection({ challenges, allStatTypes, onAdd, onRemove, onUpdate, errors }: ChallengesSectionProps) {
    return (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Challenges</h2>
                <button
                    type="button"
                    onClick={onAdd}
                    className="px-3 py-1 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                >
                    + Add Challenge
                </button>
            </div>

            {/* Difficulty Reference Card */}
            <div className="mb-4 p-4 bg-slate-700/50 border border-slate-600 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                    <span className="text-purple-400">ℹ</span>
                    <h3 className="text-sm font-medium text-gray-300">Difficulty Reference</h3>
                </div>
                <p className="text-xs text-gray-400 mb-3">
                    Challenge rating is based on the sum total of all difficulty values.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 rounded text-xs">
                        <span className="text-green-400 font-medium">Easy</span>
                        <span className="text-gray-400">0–14</span>
                    </div>
                    <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 rounded text-xs">
                        <span className="text-blue-400 font-medium">Normal</span>
                        <span className="text-gray-400">15–34</span>
                    </div>
                    <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 rounded text-xs">
                        <span className="text-yellow-400 font-medium">Hard</span>
                        <span className="text-gray-400">35–54</span>
                    </div>
                    <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 rounded text-xs">
                        <span className="text-orange-400 font-medium">Very Hard</span>
                        <span className="text-gray-400">55–74</span>
                    </div>
                    <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 rounded text-xs">
                        <span className="text-red-400 font-medium">Insane</span>
                        <span className="text-gray-400">75–99</span>
                    </div>
                    <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 rounded text-xs">
                        <span className="text-purple-400 font-medium">Impossible</span>
                        <span className="text-gray-400">100+</span>
                    </div>
                </div>
            </div>

            {challenges.length === 0 ? (
                <p className="text-gray-400 text-sm">No challenges added. Click "Add Challenge" to add one.</p>
            ) : (
                <div className="space-y-3">
                    {challenges.map((challenge, index) => (
                        <div key={index} className="flex items-center gap-4 p-3 bg-slate-700 rounded">
                            <div className="flex-1">
                                <label className="block text-xs text-gray-400 mb-1">Stat/Skill</label>
                                <select
                                    value={challenge.stat_type}
                                    onChange={(e) => onUpdate(index, 'stat_type', e.target.value)}
                                    className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                >
                                    <optgroup label="Stats">
                                        {allStatTypes.filter(s => s.type === 'stat').map(s => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </optgroup>
                                    <optgroup label="Skills">
                                        {allStatTypes.filter(s => s.type === 'skill').map(s => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </optgroup>
                                </select>
                            </div>
                            <div className="w-24">
                                <label className="block text-xs text-gray-400 mb-1">Difficulty</label>
                                <input
                                    type="number"
                                    value={challenge.difficulty}
                                    onChange={(e) => onUpdate(index, 'difficulty', parseInt(e.target.value) || 0)}
                                    min={0}
                                    max={100}
                                    className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => onRemove(index)}
                                className="px-2 py-1 text-red-400 hover:text-red-300 transition-colors"
                                title="Remove challenge"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {errors.challenges && <p className="mt-2 text-sm text-red-400">{errors.challenges}</p>}
        </div>
    );
}


/**
 * Rewards section component.
 */
interface RewardsSectionProps {
    rewards: Reward[];
    rewardTypes: RewardTypeOption[];
    rarities: RarityOption[];
    itemRecipes: ItemRecipeOption[];
    allTags: TagOption[];
    enclaves: EnclaveOption[];
    onAdd: () => void;
    onRemove: (index: number) => void;
    onUpdate: (index: number, field: keyof Reward, value: unknown) => void;
    errors: Record<string, string>;
}

function RewardsSection({ rewards, rewardTypes, rarities, itemRecipes, allTags, enclaves, onAdd, onRemove, onUpdate, errors }: RewardsSectionProps) {
    return (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Rewards</h2>
                <button
                    type="button"
                    onClick={onAdd}
                    className="px-3 py-1 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                >
                    + Add Reward
                </button>
            </div>

            {/* Reward Guidelines Card */}
            <div className="mb-4 p-4 bg-slate-700/50 border border-slate-600 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                    <span className="text-purple-400">ℹ</span>
                    <h3 className="text-sm font-medium text-gray-300">Reward Guidelines</h3>
                </div>
                <p className="text-xs text-gray-400 mb-4">
                    Rewards are granted on success. Critical success doubles the reward.
                    Partial success grants a percentage based on how close the roll was.
                </p>

                {/* Reward Tiers Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="border-b border-slate-600">
                                <th className="text-left py-2 pr-3 text-gray-400 font-medium">Difficulty</th>
                                <th className="text-left py-2 px-3 text-gray-400 font-medium">Materials</th>
                                <th className="text-left py-2 px-3 text-gray-400 font-medium">XP</th>
                                <th className="text-left py-2 pl-3 text-gray-400 font-medium">Item Drops</th>
                            </tr>
                        </thead>
                        <tbody className="text-gray-300">
                            <tr className="border-b border-slate-700/50">
                                <td className="py-2 pr-3">
                                    <span className="text-green-400 font-medium">Easy</span>
                                    <span className="text-gray-500 ml-1">(0–14)</span>
                                </td>
                                <td className="py-2 px-3">1–20</td>
                                <td className="py-2 px-3">25</td>
                                <td className="py-2 pl-3">
                                    <span className="text-gray-400">50%</span> <span className="text-gray-300">Common</span>
                                    <span className="mx-1 text-gray-600">·</span>
                                    <span className="text-gray-400">20%</span> <span className="text-green-400">Uncommon</span>
                                    <span className="mx-1 text-gray-600">·</span>
                                    <span className="text-gray-400">5%</span> <span className="text-blue-400">Rare</span>
                                </td>
                            </tr>
                            <tr className="border-b border-slate-700/50">
                                <td className="py-2 pr-3">
                                    <span className="text-blue-400 font-medium">Normal</span>
                                    <span className="text-gray-500 ml-1">(15–34)</span>
                                </td>
                                <td className="py-2 px-3">20–40</td>
                                <td className="py-2 px-3">50</td>
                                <td className="py-2 pl-3">
                                    <span className="text-gray-400">100%</span> <span className="text-gray-300">Common</span>
                                    <span className="mx-1 text-gray-600">·</span>
                                    <span className="text-gray-400">50%</span> <span className="text-green-400">Uncommon</span>
                                    <span className="mx-1 text-gray-600">·</span>
                                    <span className="text-gray-400">10%</span> <span className="text-blue-400">Rare</span>
                                </td>
                            </tr>
                            <tr className="border-b border-slate-700/50">
                                <td className="py-2 pr-3">
                                    <span className="text-yellow-400 font-medium">Hard</span>
                                    <span className="text-gray-500 ml-1">(35–54)</span>
                                </td>
                                <td className="py-2 px-3">40–60 <span className="text-gray-500">×2</span></td>
                                <td className="py-2 px-3">100</td>
                                <td className="py-2 pl-3">
                                    <span className="text-gray-400">100%</span> <span className="text-green-400">Uncommon</span>
                                    <span className="mx-1 text-gray-600">·</span>
                                    <span className="text-gray-400">20%</span> <span className="text-blue-400">Rare</span>
                                </td>
                            </tr>
                            <tr className="border-b border-slate-700/50">
                                <td className="py-2 pr-3">
                                    <span className="text-orange-400 font-medium">Very Hard</span>
                                    <span className="text-gray-500 ml-1">(55–74)</span>
                                </td>
                                <td className="py-2 px-3">60–80 <span className="text-gray-500">×2</span></td>
                                <td className="py-2 px-3">200</td>
                                <td className="py-2 pl-3">
                                    <span className="text-gray-400">50%</span> <span className="text-blue-400">Rare</span>
                                    <span className="mx-1 text-gray-600">·</span>
                                    <span className="text-gray-400">20%</span> <span className="text-purple-400">Epic</span>
                                </td>
                            </tr>
                            <tr className="border-b border-slate-700/50">
                                <td className="py-2 pr-3">
                                    <span className="text-red-400 font-medium">Insane</span>
                                    <span className="text-gray-500 ml-1">(75–99)</span>
                                </td>
                                <td className="py-2 px-3">80–100 <span className="text-gray-500">×3</span></td>
                                <td className="py-2 px-3">400</td>
                                <td className="py-2 pl-3">
                                    <span className="text-gray-400">100%</span> <span className="text-blue-400">Rare</span>
                                    <span className="mx-1 text-gray-600">·</span>
                                    <span className="text-gray-400">50%</span> <span className="text-purple-400">Epic</span>
                                </td>
                            </tr>
                            <tr>
                                <td className="py-2 pr-3">
                                    <span className="text-purple-400 font-medium">Impossible</span>
                                    <span className="text-gray-500 ml-1">(100+)</span>
                                </td>
                                <td className="py-2 px-3">100–120 <span className="text-gray-500">×3</span></td>
                                <td className="py-2 px-3">800</td>
                                <td className="py-2 pl-3">
                                    <span className="text-gray-400">100%</span> <span className="text-purple-400">Epic</span>
                                    <span className="mx-1 text-gray-600">·</span>
                                    <span className="text-gray-400">5%</span> <span className="text-orange-400">Orange</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {rewards.length === 0 ? (
                <p className="text-gray-400 text-sm">No rewards added. Click "Add Reward" to add one.</p>
            ) : (
                <div className="space-y-4">
                    {rewards.map((reward, index) => (
                        <RewardRow
                            key={index}
                            reward={reward}
                            index={index}
                            rewardTypes={rewardTypes}
                            rarities={rarities}
                            itemRecipes={itemRecipes}
                            allTags={allTags}
                            enclaves={enclaves}
                            onUpdate={onUpdate}
                            onRemove={onRemove}
                        />
                    ))}
                </div>
            )}
            {errors.rewards && <p className="mt-2 text-sm text-red-400">{errors.rewards}</p>}
        </div>
    );
}

/**
 * Single reward row component.
 */
interface RewardRowProps {
    reward: Reward;
    index: number;
    rewardTypes: RewardTypeOption[];
    rarities: RarityOption[];
    itemRecipes: ItemRecipeOption[];
    allTags: TagOption[];
    enclaves: EnclaveOption[];
    onUpdate: (index: number, field: keyof Reward, value: unknown) => void;
    onRemove: (index: number) => void;
}

function RewardRow({ reward, index, rewardTypes, rarities, itemRecipes, allTags, enclaves, onUpdate, onRemove }: RewardRowProps) {
    // Toggle a tag for this reward
    const toggleTag = (tagName: string) => {
        const newTags = reward.tags.includes(tagName)
            ? reward.tags.filter(t => t !== tagName)
            : [...reward.tags, tagName];
        onUpdate(index, 'tags', newTags);
    };

    return (
        <div className="p-4 bg-slate-700 rounded space-y-3">
            <div className="flex items-start gap-4">
                {/* Reward Type */}
                <div className="w-32">
                    <label className="block text-xs text-gray-400 mb-1">Type</label>
                    <select
                        value={reward.reward_type}
                        onChange={(e) => onUpdate(index, 'reward_type', e.target.value)}
                        className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                    >
                        {rewardTypes.map(rt => (
                            <option key={rt.value} value={rt.value}>{rt.label}</option>
                        ))}
                    </select>
                </div>

                {/* Item-specific fields */}
                {reward.reward_type === 'item' && (
                    <>
                        <div className="flex-1">
                            <label className="block text-xs text-gray-400 mb-1">Item Recipe (optional)</label>
                            <select
                                value={reward.item_recipe_id || ''}
                                onChange={(e) => onUpdate(index, 'item_recipe_id', e.target.value ? parseInt(e.target.value) : null)}
                                className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                            >
                                <option value="">Random from tags/rarity</option>
                                {itemRecipes.map(r => (
                                    <option key={r.id} value={r.id}>{r.name} ({r.rarity})</option>
                                ))}
                            </select>
                        </div>
                        <div className="w-32">
                            <label className="block text-xs text-gray-400 mb-1">Rarity Filter</label>
                            <select
                                value={reward.item_rarity || ''}
                                onChange={(e) => onUpdate(index, 'item_rarity', e.target.value || null)}
                                className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                            >
                                <option value="">Any</option>
                                {rarities.map(r => (
                                    <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                            </select>
                        </div>
                    </>
                )}

                {/* Blueprint-specific fields */}
                {reward.reward_type === 'blueprint' && (
                    <>
                        <div className="flex-1">
                            <label className="block text-xs text-gray-400 mb-1">Blueprint Recipe (optional)</label>
                            <select
                                value={reward.item_recipe_id || ''}
                                onChange={(e) => onUpdate(index, 'item_recipe_id', e.target.value ? parseInt(e.target.value) : null)}
                                className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                            >
                                <option value="">Random from tags/rarity</option>
                                {itemRecipes.filter(r => r.learnable_by === 'blueprint').map(r => (
                                    <option key={r.id} value={r.id}>{r.name} ({r.rarity})</option>
                                ))}
                            </select>
                        </div>
                        <div className="w-32">
                            <label className="block text-xs text-gray-400 mb-1">Rarity Filter</label>
                            <select
                                value={reward.item_rarity || ''}
                                onChange={(e) => onUpdate(index, 'item_rarity', e.target.value || null)}
                                className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                            >
                                <option value="">Any</option>
                                {rarities.map(r => (
                                    <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                            </select>
                        </div>
                    </>
                )}

                {/* Material-specific fields - dropdown select */}
                {reward.reward_type === 'material' && (
                    <div className="flex-1">
                        <label className="block text-xs text-gray-400 mb-1">Material Type</label>
                        <select
                            value={reward.material_type || ''}
                            onChange={(e) => onUpdate(index, 'material_type', e.target.value || null)}
                            className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                        >
                            <option value="">Select material type...</option>
                            {MATERIAL_TYPES.map(mt => (
                                <option key={mt.value} value={mt.value}>{mt.label}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Reputation-specific fields */}
                {reward.reward_type === 'reputation' && (
                    <>
                        <div className="flex-1">
                            <label className="block text-xs text-gray-400 mb-1">Enclave</label>
                            {enclaves.length === 0 ? (
                                <p className="text-xs text-yellow-400">No enclaves available. Create an enclave hex first.</p>
                            ) : (
                                <select
                                    value={reward.enclave_id || ''}
                                    onChange={(e) => onUpdate(index, 'enclave_id', e.target.value ? parseInt(e.target.value) : null)}
                                    className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                >
                                    <option value="">Select an enclave...</option>
                                    {enclaves.map(e => (
                                        <option key={e.id} value={e.id}>{e.owner_name || e.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <div className="w-24">
                            <label className="block text-xs text-gray-400 mb-1">Min Rep</label>
                            <input
                                type="number"
                                value={reward.reputation_min ?? ''}
                                onChange={(e) => onUpdate(index, 'reputation_min', e.target.value ? parseInt(e.target.value) : null)}
                                className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                placeholder="1"
                            />
                        </div>
                        <div className="w-24">
                            <label className="block text-xs text-gray-400 mb-1">Max Rep</label>
                            <input
                                type="number"
                                value={reward.reputation_max ?? ''}
                                onChange={(e) => onUpdate(index, 'reputation_max', e.target.value ? parseInt(e.target.value) : null)}
                                className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                placeholder="10"
                            />
                        </div>
                    </>
                )}

                {/* Quantity - only for material and xp */}
                {(reward.reward_type === 'material' || reward.reward_type === 'xp') && (
                    <>
                        <div className="w-20">
                            <label className="block text-xs text-gray-400 mb-1">Min Qty</label>
                            <input
                                type="number"
                                value={reward.quantity_min}
                                onChange={(e) => onUpdate(index, 'quantity_min', parseInt(e.target.value) || 1)}
                                min={1}
                                className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                            />
                        </div>
                        <div className="w-20">
                            <label className="block text-xs text-gray-400 mb-1">Max Qty</label>
                            <input
                                type="number"
                                value={reward.quantity_max}
                                onChange={(e) => onUpdate(index, 'quantity_max', parseInt(e.target.value) || 1)}
                                min={1}
                                className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                            />
                        </div>
                    </>
                )}

                {/* Chance */}
                <div className="w-20">
                    <label className="block text-xs text-gray-400 mb-1">Chance %</label>
                    <input
                        type="number"
                        value={reward.chance_percent}
                        onChange={(e) => onUpdate(index, 'chance_percent', parseInt(e.target.value) || 100)}
                        min={1}
                        max={100}
                        className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                    />
                </div>

                {/* Remove button */}
                <button
                    type="button"
                    onClick={() => onRemove(index)}
                    className="mt-5 px-2 py-1 text-red-400 hover:text-red-300 transition-colors"
                    title="Remove reward"
                >
                    ✕
                </button>
            </div>

            {/* Tags for item/blueprint rewards */}
            {(reward.reward_type === 'item' || reward.reward_type === 'blueprint') && !reward.item_recipe_id && (
                <div>
                    <label className="block text-xs text-gray-400 mb-1">Filter by Tags (optional)</label>
                    <div className="flex flex-wrap gap-1">
                        {allTags.map(tag => (
                            <button
                                key={tag.id}
                                type="button"
                                onClick={() => toggleTag(tag.name)}
                                className={`px-2 py-0.5 text-xs rounded transition-colors ${
                                    reward.tags.includes(tag.name)
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-slate-600 text-gray-300 hover:bg-slate-500'
                                }`}
                            >
                                {tag.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}


/**
 * Requirements section component.
 */
interface RequirementsSectionProps {
    requirements: Requirement[];
    itemRecipes: ItemRecipeOption[];
    enclaves: EnclaveOption[];
    onAdd: () => void;
    onRemove: (index: number) => void;
    onUpdate: (index: number, field: keyof Requirement, value: unknown) => void;
    onTypeChange: (index: number, type: 'item' | 'reputation' | 'reputation_max') => void;
    errors: Record<string, string>;
}

function RequirementsSection({ requirements, itemRecipes, enclaves, onAdd, onRemove, onUpdate, onTypeChange, errors }: RequirementsSectionProps) {
    return (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Requirements</h2>
                <button
                    type="button"
                    onClick={onAdd}
                    className="px-3 py-1 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                >
                    + Add Requirement
                </button>
            </div>

            {/* Requirements Info Card */}
            <div className="mb-4 p-4 bg-slate-700/50 border border-slate-600 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-purple-400">ℹ</span>
                    <h3 className="text-sm font-medium text-gray-300">About Requirements</h3>
                </div>
                <p className="text-xs text-gray-400">
                    Requirements control who can see and attempt this adventure. Players must meet ALL requirements to access the adventure.
                    Item requirements check if the player has the item in their vault. Reputation requirements check if they have enough standing with an enclave.
                </p>
            </div>

            {requirements.length === 0 ? (
                <p className="text-gray-400 text-sm">No requirements. Anyone can attempt this adventure.</p>
            ) : (
                <div className="space-y-3">
                    {requirements.map((requirement, index) => (
                        <div key={index} className="flex items-start gap-4 p-3 bg-slate-700 rounded">
                            {/* Requirement Type */}
                            <div className="w-40">
                                <label className="block text-xs text-gray-400 mb-1">Type</label>
                                <select
                                    value={requirement.type}
                                    onChange={(e) => onTypeChange(index, e.target.value as 'item' | 'reputation' | 'reputation_max')}
                                    className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                >
                                    <option value="item">Item</option>
                                    <option value="reputation">Reputation Min</option>
                                    <option value="reputation_max">Reputation Max</option>
                                </select>
                            </div>

                            {/* Item requirement fields */}
                            {requirement.type === 'item' && (
                                <div className="flex-1">
                                    <label className="block text-xs text-gray-400 mb-1">Required Item</label>
                                    <select
                                        value={requirement.item_id || ''}
                                        onChange={(e) => onUpdate(index, 'item_id', e.target.value ? parseInt(e.target.value) : null)}
                                        className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                    >
                                        <option value="">Select an item...</option>
                                        {itemRecipes.map(r => (
                                            <option key={r.id} value={r.id}>{r.name} ({r.rarity})</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Reputation Min requirement fields */}
                            {requirement.type === 'reputation' && (
                                <>
                                    <div className="flex-1">
                                        <label className="block text-xs text-gray-400 mb-1">Enclave</label>
                                        {enclaves.length === 0 ? (
                                            <p className="text-xs text-yellow-400">No enclaves available. Create an enclave hex first.</p>
                                        ) : (
                                            <select
                                                value={requirement.enclave_id || ''}
                                                onChange={(e) => onUpdate(index, 'enclave_id', e.target.value ? parseInt(e.target.value) : null)}
                                                className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                            >
                                                <option value="">Select an enclave...</option>
                                                {enclaves.map(e => (
                                                    <option key={e.id} value={e.id}>{e.owner_name || e.name}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    <div className="w-40">
                                        <label className="block text-xs text-gray-400 mb-1">Min Level</label>
                                        <select
                                            value={requirement.minimum_reputation}
                                            onChange={(e) => onUpdate(index, 'minimum_reputation', parseInt(e.target.value))}
                                            className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                        >
                                            {REPUTATION_LEVELS.map(level => (
                                                <option key={level.value} value={level.value}>{level.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}

                            {/* Reputation Max requirement fields - hides adventure when player exceeds this level */}
                            {requirement.type === 'reputation_max' && (
                                <>
                                    <div className="flex-1">
                                        <label className="block text-xs text-gray-400 mb-1">Enclave</label>
                                        {enclaves.length === 0 ? (
                                            <p className="text-xs text-yellow-400">No enclaves available. Create an enclave hex first.</p>
                                        ) : (
                                            <select
                                                value={requirement.enclave_id || ''}
                                                onChange={(e) => onUpdate(index, 'enclave_id', e.target.value ? parseInt(e.target.value) : null)}
                                                className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                            >
                                                <option value="">Select an enclave...</option>
                                                {enclaves.map(e => (
                                                    <option key={e.id} value={e.id}>{e.owner_name || e.name}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    <div className="w-40">
                                        <label className="block text-xs text-gray-400 mb-1">Max Level</label>
                                        <select
                                            value={requirement.maximum_reputation}
                                            onChange={(e) => onUpdate(index, 'maximum_reputation', parseInt(e.target.value))}
                                            className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                        >
                                            {REPUTATION_LEVELS.map(level => (
                                                <option key={level.value} value={level.value}>{level.label}</option>
                                            ))}
                                        </select>
                                        <p className="text-xs text-gray-500 mt-1">Hidden when player goes above this level</p>
                                    </div>
                                </>
                            )}

                            {/* Remove button */}
                            <button
                                type="button"
                                onClick={() => onRemove(index)}
                                className="mt-5 px-2 py-1 text-red-400 hover:text-red-300 transition-colors"
                                title="Remove requirement"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {errors.requirements && <p className="mt-2 text-sm text-red-400">{errors.requirements}</p>}
        </div>
    );
}
